import React, {
  forwardRef, useMemo, memo, useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { func } from 'prop-types';
import l18n from '../../shared/strings';
import Logger from '../../services/Logger';

import {
  isUploadStarted,
  requiredFields as requiredCustomFieldsSelector,
} from '../../store/import/selectors';
import { getIndicator, panelsVisibilityShape } from './helpers';
import DetailsBlock from '../DetailsBlock';
import Description from '../details/view/Description';

/** @NOTE: Used require just for conditional import */
const { changeField } = window.inbox
  ? require('../../store/inboxApp/actions')
  : require('../../store/import/actions');

const TitleAndDescription = forwardRef(({ panelsVisibility, toggleVisibility }, ref) => {
  const dispatch = useDispatch();
  const numberOfSelectedFiles = useSelector((state) => state.import.selected.length);
  const title = useSelector((state) => state.import.fields.title || '');
  const description = useSelector((state) => state.import.fields.description || '');
  const titleError = useSelector((state) => state.import.errors.title);
  const descriptionError = useSelector((state) => state.import.errors.description);
  const requiredFields = useSelector(requiredCustomFieldsSelector);
  const permissions = useSelector((state) => {
    const { editAssetDescription, editAssetTitle } = state.import.permissions;
    return { editAssetTitle, editAssetDescription };
  });
  const disabled = useSelector(isUploadStarted);

  const indicator = useMemo(() => getIndicator('titleAndDescription', requiredFields), [requiredFields]);

  const handleChangeTitle = useCallback((_, value) => {
    dispatch(changeField({ title: 'title', value }));
    Logger.log('User', 'UploadPanelChangeTitle', { assets_number: numberOfSelectedFiles });
  }, [dispatch, numberOfSelectedFiles]);

  const handleChangeDescription = useCallback((_, value) => {
    dispatch(changeField({ title: 'description', value }));
    Logger.log('User', 'UploadPanelChangeDesc', { assets_number: numberOfSelectedFiles });
  }, [dispatch, numberOfSelectedFiles]);

  return (
    <DetailsBlock
      dataQa="description"
      ref={ref}
      detailsPanelVisibility={panelsVisibility}
      toggleVisibility={toggleVisibility}
      blockName="importDescriptionVisibility"
      blockTitle={l18n.DETAILS.textTitleAndDescription}
      indicator={indicator}
      error={Boolean(titleError || descriptionError)}
      errorHighlight
      openByDefault={titleError || descriptionError || indicator !== null}
    >
      <Description
        logEvents={false}
        title={title}
        description={description}
        selectedAssetsIds={['']}
        titleShow
        titleEditable={permissions.editAssetTitle}
        descriptionShow
        descriptionEditable={permissions.editAssetDescription}
        textareaHeightNameLS="picsio.importDescriptionHeight"
        changeTitle={handleChangeTitle}
        changeDescription={handleChangeDescription}
        disabled={disabled}
        titleError={titleError}
        descriptionError={descriptionError}
      />
    </DetailsBlock>
  );
});

TitleAndDescription.propTypes = {
  panelsVisibility: panelsVisibilityShape.isRequired,
  toggleVisibility: func.isRequired,
};

export default memo(TitleAndDescription);
